import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Package, FileText, ChevronRight, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useReorder } from "@/lib/reorder";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import MySessionsCard from "@/components/MySessionsCard";
import MyGarbhaCard from "@/components/MyGarbhaCard";

export default function Dashboard() {
  const { user } = useAuth();
  const { reorder, busy } = useReorder();
  const [appts, setAppts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [rx, setRx] = useState([]);

  useEffect(() => {
    api.get("/appointments").then(({data})=>setAppts(data)).catch(()=>{});
    api.get("/orders").then(({data})=>setOrders(data)).catch(()=>{});
    api.get("/prescriptions").then(({data})=>setRx(data)).catch(()=>{});
  }, []);

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
      <div className="overline mb-2">Patient Portal</div>
      <h1 className="font-display text-4xl lg:text-5xl mb-2">Namaste, {user?.name?.split(" ")[0]}</h1>
      <p className="text-muted-foreground mb-10">Your appointments, orders and prescriptions — all in one place.</p>

      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        {[
          {icon: Calendar, label: "Appointments", val: appts.length, color: "bg-emerald-50"},
          {icon: Package, label: "Orders", val: orders.length, color: "bg-amber-50"},
          {icon: FileText, label: "Prescriptions", val: rx.length, color: "bg-rose-50"},
        ].map((s)=>(
          <Card key={s.label} className="border-border">
            <CardContent className="p-6 flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl ${s.color} flex items-center justify-center text-primary`}><s.icon size={20}/></div>
              <div><div className="font-display text-3xl">{s.val}</div><div className="text-xs text-muted-foreground">{s.label}</div></div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="appointments">
        <TabsList className="bg-muted">
          <TabsTrigger value="appointments" data-testid="db-tab-appts">Appointments</TabsTrigger>
          <TabsTrigger value="orders" data-testid="db-tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="prescriptions" data-testid="db-tab-rx">Prescriptions</TabsTrigger>
        </TabsList>

        <TabsContent value="appointments" className="mt-6 space-y-3">
          {appts.length === 0 && <p className="text-muted-foreground text-sm">No appointments yet.</p>}
          {appts.map((a)=>(
            <Card key={a.id} className="border-border">
              <CardContent className="p-5 flex items-center justify-between">
                <div>
                  <div className="font-display text-xl">{a.date} · {a.time_slot}</div>
                  <div className="text-sm text-muted-foreground capitalize">{a.consultation_type} · {a.reason}</div>
                </div>
                <Badge variant={a.status === "confirmed" ? "default" : "secondary"} className="capitalize">{a.status}</Badge>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="orders" className="mt-6 space-y-3">
          {orders.length === 0 && <p className="text-muted-foreground text-sm">No orders yet.</p>}
          {orders.map((o)=>(
            <Card key={o.id} className="border-border">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-display text-xl">Order #{o.id.slice(0,8)}</div>
                    <div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">₹{o.total?.toFixed(2)}</div>
                    <Badge variant={o.status === "delivered" ? "default" : "secondary"} className="capitalize">{o.status}</Badge>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">{o.items?.map(i=>`${i.name}×${i.quantity}`).join(", ")}</div>
                <div className="flex items-center gap-4 mt-3">
                  <Link
                    to={`/orders/${o.id}`}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    data-testid={`db-order-details-${o.id}`}
                  >
                    View full details <ChevronRight size={12}/>
                  </Link>
                  <Button
                    variant="outline"
                    onClick={() => reorder(o.id)}
                    disabled={busy === o.id}
                    className="btn-pill h-8 text-xs border-border ml-auto"
                    data-testid={`db-order-reorder-${o.id}`}
                  >
                    <RotateCcw size={12} className="mr-1.5"/> {busy === o.id ? "Adding…" : "Order again"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="prescriptions" className="mt-6 space-y-3">
          {rx.length === 0 && <p className="text-muted-foreground text-sm">No prescriptions yet.</p>}
          {rx.map((r)=>(
            <Card key={r.id} className="border-border">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-display text-xl">{r.diagnosis}</div>
                  <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
                <div className="text-xs text-muted-foreground mb-3">by {r.doctor_name}</div>
                <div className="space-y-1">
                  {r.medicines?.map((m,i)=>(
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <ChevronRight size={12} className="text-secondary"/>
                      <span className="font-medium">{m.name}</span>
                      <span className="text-muted-foreground">— {m.dosage} ({m.duration})</span>
                    </div>
                  ))}
                </div>
                {r.instructions && <p className="text-sm mt-3 text-foreground/80 italic">"{r.instructions}"</p>}
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      <div className="mt-10 space-y-6">
        <MyGarbhaCard />
        <MySessionsCard />
        <ChangePasswordCard />
      </div>
    </main>
  );
}
